/**
 * Voucher stats and classification helpers.
 * "now" uses browser local time (Date.now()).
 */

export interface VoucherLike {
  valid_from: string;
  valid_until: string;
  is_active?: boolean;
  total_used?: number;
  max_total_usage?: number;
}

function parseVoucherDate(s: string | null | undefined): number {
  if (!s) return NaN;
  const d = new Date(s);
  return d.getTime();
}

/** Active Now: status is active AND now is between valid_from and valid_until */
export function isActiveNow(
  voucher: VoucherLike,
  nowMs: number = Date.now()
): boolean {
  const start = parseVoucherDate(voucher.valid_from);
  const end = parseVoucherDate(voucher.valid_until);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  if (voucher.is_active === false) return false;
  return nowMs >= start && nowMs <= end;
}

/** Expired: end date passed OR status explicitly inactive/disabled */
export function isExpired(
  voucher: VoucherLike,
  nowMs: number = Date.now()
): boolean {
  const end = parseVoucherDate(voucher.valid_until);
  if (Number.isNaN(end)) return true;
  if (nowMs > end) return true;
  return false;
}

/** Get redemption/usage count from voucher */
export function getRedeemedCount(voucher: VoucherLike): number {
  const v = voucher.total_used;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  return 0;
}

export interface VoucherStatsResult {
  total: number;
  activeNow: number;
  expired: number;
  totalRedeemed: number;
  hasRedeemedData: boolean;
}

/** Compute KPI stats from voucher list */
export function computeVoucherStats(
  vouchers: VoucherLike[],
  nowMs: number = Date.now()
): VoucherStatsResult {
  let activeNow = 0;
  let expired = 0;
  let totalRedeemed = 0;
  let hasRedeemedData = false;

  for (const v of vouchers) {
    if (isActiveNow(v, nowMs)) activeNow += 1;
    if (isExpired(v, nowMs)) expired += 1;
    const used = getRedeemedCount(v);
    if (used > 0) hasRedeemedData = true;
    totalRedeemed += used;
  }

  return {
    total: vouchers.length,
    activeNow,
    expired,
    totalRedeemed,
    hasRedeemedData,
  };
}
